import { FormikValues, useFormik } from 'formik';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { deleteInitPayout, getTan, postPayout } from 'src/api/stoApi';
import { BottomCard } from 'src/components/payment/rent/BottomCard';
import { getInitValuesEarningPeriod, validationSchemaEarningPeriod } from 'src/components/payment/rent/forms/EarningPeriod';
import { getInitValuesExecutionDate, validationSchemaExecutionDate } from 'src/components/payment/rent/forms/ExecutionDate';
import { getInitValuesRentSurplus, validationSchemaRentSurplus } from 'src/components/payment/rent/forms/RentSurplus';
import { PayoutSystem } from 'src/components/payment/rent/PayoutSystem';
import { TanModal } from 'src/components/payment/rent/TanModal';
import { checkIfCapitalGainsTaxesApply } from 'src/components/payment/rent/TaxSystem';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { useAssetsFiltered } from 'src/hooks/useAssetsFiltered';
import { useMangoAccountInfo } from 'src/hooks/useMangoAccountInfo';
import { usePayoutBySpv } from 'src/hooks/usePayoutBySpv';
import { usePayouts } from 'src/hooks/usePayouts';
import { useSPV } from 'src/hooks/useSPV';
import { Asset, Payout, PayoutPostRequest } from 'src/models';
import { PayoutStatus } from 'src/models/enum';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { BackendResponse, PostPayout } from 'src/models/index';
import { Routes } from 'src/router/Routes';
import { availableAccountBalance, awaitUtil, payoutFilterPendingInitQueued } from 'src/util/utils';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { Alert, Card, CardContent, MenuItem, TextField, Typography } from '@mui/material';

export const RentPayment = () => {
  const { t } = useTranslation();
  const { spvId } = useParams();
  const assets = useAssetsFiltered([TAssetStage.secondary], spvId);
  const [assetId, setAssetId] = useState<string>('');
  const [futurePayoutDate, setFuturePayoutDate] = useState<string>();
  const [pastPayoutDate, setPastPayoutDate] = useState<string>();
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(undefined);
  const [showTanModal, setShowTanModal] = useState<boolean>(false);
  const [payoutId, setPayoutId] = useState<string>('');
  const spv = useSPV(spvId);
  const payoutByAllSpvs = spvId && usePayoutBySpv([spvId], [PayoutStatus.init, PayoutStatus.pending]);
  const payoutBySelectedSpv = spvId && payoutByAllSpvs ? Object.values(payoutByAllSpvs[spvId]) : null;
  const mangoPay = useMangoAccountInfo(spv?.paymentProvider.mangopayId ?? '', spv?.paymentProvider.mangopayWalletId ?? '');
  const accountBalance = Number(mangoPay?.balance) ?? 0;
  const availableBalance = availableAccountBalance(payoutBySelectedSpv, accountBalance, false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasErrors, setHasErrors] = useState(false);
  const [payoutToDispatch, setPayoutToDispatch] = useState<PayoutPostRequest>({
    executionDate: '',
    rentFromLastPayment: '',
    rentPerShareRounded: '',
    rentSurplus: 0,
    rentalAmount: '',
    retainedForNextMonth: '',
    retainedPercentage: 0,
  });
  const [buttonLoading, setButtonLoading] = useState(false);

  const dropdown = (options: Asset[], assetId: string, setAssetId: Dispatch<SetStateAction<string>>) => {
    return (
      <TextField
        margin="normal"
        label={t('issuer.rent.dropdown.label')}
        type="text"
        variant="outlined"
        fullWidth
        value={assetId}
        onChange={(val) => setAssetId(val.target.value)}
        select
        style={{ textAlign: 'left' }}
        data-testid="asset-dropdown"
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id} data-testid={option.id}>
            {option.descriptions.title}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  // Close modal before payout has been validated, deletes the init payout.
  const handleCloseTanModal = async () => {
    setShowTanModal(false);
    await awaitUtil(deleteInitPayout(payoutId));
  };

  useEffect(() => {
    const assetIdFromUrl = searchParams.get('assetId');
    if (assetIdFromUrl && assets) {
      setAssetId(assetIdFromUrl);
    }
  }, [assets]);

  useEffect(() => {
    if (assetId && assets) {
      setAssetId(assetId);
      const asset = assets.find((a) => a.id === assetId);
      if (asset) setSelectedAsset(asset);
      setSearchParams({ assetId: assetId });
    }
  }, [assetId]);

  const payouts = usePayouts(assetId);
  useEffect(() => {
    if (payouts) checkForFuturePendingPayout(payouts);
    if (payouts) checkForPastPayoutThreeWeeks(payouts);
  }, [payouts]);

  const formikExecutionDate = useFormik({
    enableReinitialize: true,
    initialValues: getInitValuesExecutionDate(),
    validationSchema: validationSchemaExecutionDate,
    validateOnMount: true,
    onSubmit: (val) => {
      const payout = payoutToDispatch;
      payout.executionDate = val.executionDate;
      setPayoutToDispatch(payout);
    },
  });

  const capitalGainsTaxes = checkIfCapitalGainsTaxesApply(selectedAsset?.investmentDetails.securityType);
  const formikEarningPeriod = useFormik({
    // should change this when payment is yearly or weekly
    enableReinitialize: true,
    initialValues: getInitValuesEarningPeriod(formikExecutionDate?.values?.executionDate),
    validationSchema: validationSchemaEarningPeriod,
    validateOnMount: true,
    onSubmit: (val: FormikValues) => {
      if (capitalGainsTaxes) {
        const payout = payoutToDispatch;
        payout.fiscalMonth = new Date(val.earningsPeriod).getMonth().toString();
        payout.fiscalYear = new Date(val.earningsPeriod).getFullYear().toString();
        setPayoutToDispatch(payout);
      }
    },
  });

  const formikRentSurplus = useFormik({
    enableReinitialize: true,
    initialValues: getInitValuesRentSurplus(selectedAsset, availableBalance as number),
    validationSchema: validationSchemaRentSurplus(availableBalance as number),
    validateOnMount: true,

    onSubmit: (val) => {
      const payout = payoutToDispatch;
      payout.rentSurplus = Number(val.rentSurplus);
      setPayoutToDispatch(payout);
    },
  });

  const allForms = [formikExecutionDate, formikEarningPeriod, formikRentSurplus];

  const sendForm = async () => {
    // submit all forms
    for await (const form of allForms) {
      await form.validateForm();
      if (Object.keys(form.errors).length > 0) {
        await form.setTouched({ ...form.touched, ...(form.errors as any) });
        continue;
      }
    }
    // check if any form has errors
    const hasErrors = allForms.some((form) => form.errors && Object.keys(form.errors).length > 0);
    setHasErrors(hasErrors);
    if (!hasErrors) {
      for await (const form of allForms) {
        await form.submitForm();
      }
      if (!selectedAsset?.id) return;
      setButtonLoading(true);
      const [response, error] = await awaitUtil<BackendResponse<PostPayout>>(postPayout(selectedAsset?.id, payoutToDispatch));
      if (!error && response && response?.data?.data?.payoutId) {
        await getTan(response.data.data.payoutId);
        setPayoutId(response.data.data.payoutId);
        setShowTanModal(true);
        setButtonLoading(false);
      }
    }
  };

  const checkForFuturePendingPayout = (payouts: Payout[]): void => {
    const pendingPayouts = payoutFilterPendingInitQueued(payouts);
    if (pendingPayouts.length === 0) {
      setFuturePayoutDate(undefined);
      return;
    }

    // Cleanup executionDate: if string convert to date (some older assets still have a string as executionDate)
    pendingPayouts.forEach((p) => {
      if (typeof p.executionDate === 'string') {
        p.executionDate = new Date(p.executionDate);
      }
    });
    // If we wanted to only filter for future payouts. We had this in the old launchpad, but there were some problems with expired payouts.
    // const futurePendingPayouts = pendingPayouts.filter((p) => new Date(p.executionDate) > new Date());
    // console.log('futurePendingPayouts', futurePendingPayouts);
    // if (futurePendingPayouts.length === 0) {
    //   setFuturePayoutDate(undefined);
    //   return;
    // }
    // const nextFuturePendingPayout = futurePendingPayouts.sort((a, b) => a.executionDate.getTime() - b.executionDate.getTime())[0];

    const nextFuturePendingPayout = pendingPayouts.sort((a, b) => a.executionDate.getTime() - b.executionDate.getTime())[0];
    const humanReadableDate = new Date(nextFuturePendingPayout.executionDate).toLocaleDateString();

    setFuturePayoutDate(humanReadableDate);
  };

  const checkForPastPayoutThreeWeeks = (payouts: Payout[]): void => {
    // Cleanup executionDate: if string convert to date (some older assets still have a string as executionDate)
    payouts.forEach((p) => {
      if (typeof p.executionDate === 'string') {
        p.executionDate = new Date(p.executionDate);
      }
    });

    const pastPayoutsThreeWeeks = payouts.filter((p) => p.executionDate > new Date(new Date().getTime() - 3 * 7 * 24 * 60 * 60 * 1000));
    if (pastPayoutsThreeWeeks.length === 0) {
      setPastPayoutDate(undefined);
      return;
    }

    const lastPastPayout = pastPayoutsThreeWeeks.sort((a, b) => b.executionDate.getTime() - a.executionDate.getTime())[0];
    const humanReadableDate = new Date(lastPastPayout.executionDate).toLocaleDateString();
    setPastPayoutDate(humanReadableDate);
  };

  const returnNextFuturePendingPayment = (payoutDate: string | undefined) => {
    if (!payoutDate)
      return (
        <Typography variant="body1">
          <>{t('issuer.rent.noScheduledPayments')}</>
        </Typography>
      );
    return (
      <Typography variant="body1">
        <>{t('issuer.rent.nextScheduled') + ' ' + payoutDate}</>
      </Typography>
    );
  };

  // return last past payment within the past 3 weeks
  const returnLastPastPayment = (payoutDate: string | undefined) => {
    if (!payoutDate)
      return (
        <Typography variant="body1">
          <>{t('issuer.rent.noPastPayments')}</>
        </Typography>
      );
    return (
      <Typography variant="body1">
        <>{t('issuer.rent.lastPastPayment') + ' ' + payoutDate}</>
      </Typography>
    );
  };

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.PAYMENT.ROOT,
            label: t('payment.breadcrumbs.payment'),
            icon: <AccountBalanceIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: spvId ? Routes.PAYMENT.ACCOUNT.replace(':spvId', spvId) : '#',
            label: spv?.companyName ?? ' ',
          },
        ]}
        headline={spv?.companyName ?? ' '}
      />
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 0 }}>
            {t('issuer.rent.assets')}
          </Typography>
          {assets && dropdown(assets, assetId, setAssetId)}
          {payouts && returnNextFuturePendingPayment(futurePayoutDate)}
          {payouts && returnLastPastPayment(pastPayoutDate)}
          {payouts && (
            <Typography
              color="primary"
              to={Routes.ASSETS.INVESTORPAYOUT.replace(':assetId', assetId)}
              component={Link}
              sx={{ textDecoration: 'none' }}
            >
              {t('issuer.rent.viewPayouts')}
            </Typography>
          )}
        </CardContent>
      </Card>
      {selectedAsset && payouts && spv && (
        <PayoutSystem
          asset={selectedAsset}
          payouts={payouts}
          spv={spv}
          formikArray={allForms}
          payoutToDispatch={payoutToDispatch}
          setPayoutToDispatch={setPayoutToDispatch}
        />
      )}
      {selectedAsset && payouts && spv && (
        <>
          <BottomCard sendForm={sendForm} nextPayment={futurePayoutDate} previousPayment={pastPayoutDate} buttonLoading={buttonLoading} />
        </>
      )}
      {hasErrors && (
        <Alert sx={{ marginTop: '1.5rem' }} severity="error">
          {t('issuer.rent.fixError')}
        </Alert>
      )}
      <TanModal
        open={showTanModal}
        handleClose={handleCloseTanModal}
        rentPerShare={payoutToDispatch.rentPerShareRounded}
        totalAmount={payoutToDispatch.rentalAmount}
        assetId={assetId}
        payoutId={payoutId}
      />
    </>
  );
};
